import { ROUTES } from 'configs/routes/constants';
import Cookie from 'js-cookie';
import intersection from 'lodash/intersection';
import isUndefined from 'lodash/isUndefined';
import { observable, computed } from 'mobx';
import { TEMPLATES } from 'modules/configManager/constants';
import { FileLibraryProvider } from 'modules/configManager/types';

import {
  SPARKLE_COMMUNICATION_DISABLED,
  SPARKLE_COMMUNICATION_ENABLED,
  STREAMING_CONFIGS,
  STREAMING_FREE,
  STREAMING,
  STREAMING_CAMPAIGN_CANDIDATE,
  BETA_SALES_DASHBOARD,
  ATTACHMENT_UPLOAD_EXTRA_LIMIT,
  ATTACHMENT_UPLOAD_LIMIT
} from './constants';

class MembershipStore {
  constructor() {
    Object.assign(this, Cookie.getJSON(this.membershipName));
  }

  @observable academy = false;
  @observable subdomain;
  @observable active;
  @observable name = '';
  @observable template = 'classic';
  @observable currencyCode;
  @observable knowMore;
  @observable allowDiscussion;
  @observable allowHotPass;
  @observable allowSignup;
  @observable backgroundImage;
  @observable code;
  @observable configs = [];
  @observable configBetaFeatures = [];
  @observable analyticsScript;
  @observable helpEnabled;
  @observable id;
  @observable loaded = false;
  @observable logo;
  @observable logoHeader;
  @observable notifyFirstAccess;
  @observable lookAndFeel;
  @observable secondaryColor;
  @observable streamingBackgroundImages;
  @observable certificateActive;
  @observable showProgress;
  @observable trooperSchool;
  @observable hideModuleNumber;
  @observable producerData;
  @observable subscription;
  @observable membershipOwnerHash;
  @observable offers = [];
  /** @type number | null */
  @observable marketplaceId = null;
  @observable emailNotificationBeta = false;
  @observable streamingDrmProtection = false;
  @observable forceOnlineCourseLabels = false;
  @observable fileLibraryProvider = FileLibraryProvider.Club;
  @observable productHomePreviewUrl;
  onlineEvent = false;

  links = {
    content: ROUTES.CONTENT.LIST,
    ctaCommunities: ROUTES.CTA_COMMUNITIES,
    classManager: ROUTES.CLASS.LIST,
    userManager: ROUTES.USER.LIST,
    userManagerImport: ROUTES.USER.IMPORT,
    broadcastManager: ROUTES.BROADCAST.LIST,
    reports: ROUTES.REPORTS,
    community: ROUTES.COMMUNITY.LIST,
    salesManager: ROUTES.SALES,
    configManager: ROUTES.CONFIG,
    accountManager: ROUTES.ACCOUNT,
    dashboard: ROUTES.DASHBOARD
  };

  get membershipName() {
    return `membership_${this.subdomain}`;
  }

  get sessionName() {
    const parts = location.hostname.split('.');
    const subdomain = parts.shift();

    return `membershipData_${subdomain}`;
  }

  @observable hasSeason;
  // eslint-disable-next-line no-dupe-class-members
  set hasSeason(val) {
    this.hasSeason = isUndefined(val) ? false : val;
  }

  @computed
  get isStreamingEnabled() {
    return intersection(this.configs, STREAMING_CONFIGS).length > 0;
  }

  @computed
  get isSparkleCampaignEnabled() {
    return this.configs.includes(SPARKLE_COMMUNICATION_ENABLED);
  }

  @computed
  get isSparkleCampaignDisabled() {
    return this.configs.includes(SPARKLE_COMMUNICATION_DISABLED);
  }

  @computed
  get isStreamingFreeEnabled() {
    return this.configs.includes(STREAMING_FREE);
  }

  @computed
  get hasStreamingBackground() {
    return this.configs.includes('STREAMING_BACKGROUND_IMAGE');
  }

  @computed
  get hasStreamingConfig() {
    return this.configs.includes(STREAMING);
  }

  @computed
  get isStreamingFreeContentEnabled() {
    return this.configs.includes('STREAMING_FREE_CONTENT');
  }

  @computed
  get isFourGbMediaUploadLimit() {
    return this.configs.includes('PAGE_MEDIA_EXTRA_LIMIT');
  }

  @computed
  get isBeta() {
    return this.configs.includes('BETA');
  }

  @computed
  get isAttachmentUploadExtraLimit() {
    return this.configs.includes('EXTRA_LIMITS');
  }

  get attachmentUploadLimit() {
    return this.isAttachmentUploadExtraLimit
      ? ATTACHMENT_UPLOAD_EXTRA_LIMIT
      : ATTACHMENT_UPLOAD_LIMIT;
  }

  @computed
  get isLifetimeAccessEnabled() {
    return this.configs.includes('LIFETIME_ACCESS');
  }

  @computed
  get isAccessPlusEnabled() {
    return this.configs.includes('MEMBERSHIP_GROUPS_ENABLED');
  }

  @computed
  get isStreamingCampaignCandidate() {
    return this.configs.includes(STREAMING_CAMPAIGN_CANDIDATE);
  }

  @computed
  get hasBetaSalesDashboardConfig() {
    return this.configs.includes(BETA_SALES_DASHBOARD);
  }

  @computed
  get isOnlineEventTemplate() {
    return this.template === TEMPLATES.ONLINE_EVENT;
  }

  @computed
  get productId() {
    return this.marketplaceId;
  }

  hasBetaFeature = (betaFeature) => {
    return this.configBetaFeatures?.includes(betaFeature);
  };
}

export default new MembershipStore();
